import {
  Autocomplete,
  Box,
  FormHelperText,
  IconButton,
  Tooltip,
} from '@mui/material';
import { ErrorPresentation } from 'helpers/validators';
import { formatTranslation, unsafeTranslationKey } from 'intl';
import { CommonTranslationKeys } from 'intl/keys';
import {
  FocusEventHandler,
  ReactElement,
  Ref,
  SyntheticEvent,
  forwardRef,
  memo,
  useCallback,
  MouseEvent,
  HTMLAttributes,
  ReactNode,
} from 'react';
import { useResettableState } from 'hooks/useResettableState';
import { useIntl } from 'react-intl';
import {
  getAutocompleteRenderInput,
  getHelperTextFromError,
} from './common';
import ClearIcon from '@mui/icons-material/Clear';
import { LocalStorageManager } from 'services/storage/LocalStorageManager';

const MemoAutocomplete = memo(Autocomplete);

interface AutocompleteWithTextProps {
  autoFocus?: boolean;
  handleBlur?: FocusEventHandler<HTMLDivElement>;
  handleChange: (event: any, value: string | null | undefined) => void;
  labelId: string;
  disabled?: boolean | { reason: string };
  error: ErrorPresentation;
  required?: boolean;
  minRows?: number;
  fieldName: string;
  isHalf?: boolean;
  defaultValue?: string;
  dense: boolean;
  reserveSpaceForHelperText: boolean;
  multiline?: boolean;
  placeholder?: string;
}

function AutocompleteWithText(
  {
    autoFocus,
    labelId,
    required,
    disabled,
    handleChange,
    handleBlur,
    error,
    minRows,
    fieldName,
    isHalf,
    defaultValue,
    dense,
    reserveSpaceForHelperText,
    multiline,
    placeholder,
  }: AutocompleteWithTextProps,
  ref: Ref<HTMLDivElement>
): ReactElement {
  const intl = useIntl();
  if (!fieldName) {
    throw new Error('fieldName is required');
  }

  const id = `autocomplete-${fieldName}`; // DOM id
  // Values saved for the field; read again for another field.
  const [options, setOptions] = useResettableState(fieldName, () =>
    LocalStorageManager.getAutocompleteValues(fieldName)
  );

  const sx = {
    fontFamily: 'Archivo, Arial',
    width: '100%',
    '.MuiFormLabel-root': {
      fontFamily: 'Archivo, Arial',
      bottom: '1rem',
      lineHeight: 1,
    },
    '.MuiInputBase-input': {
      height: '1rem',
    },
  };

  const helperText = getHelperTextFromError(error, intl);

  const renderInput = getAutocompleteRenderInput({
    autoFocus,
    id,
    required: !!required,
    error,
    disabled: !!disabled,
    label: labelId ? formatTranslation(intl, unsafeTranslationKey(labelId) ) : '',
    placeholder,
    dense,
    multiline,
    minRows,
  });

  const onBlur: FocusEventHandler<HTMLDivElement> = useCallback(
    (event): void => {
      const value = (event.target as HTMLInputElement).value;
      if (typeof value === 'string' && value.length > 0) {
        LocalStorageManager.addAutocompleteValue(fieldName, value);
        // Update options after adding a new value
        setOptions(LocalStorageManager.getAutocompleteValues(fieldName));
      }
      handleBlur?.(event);
    },
    [fieldName, handleBlur]
  );

  const unifiedHandleChange = useCallback(
    (event: SyntheticEvent<Element, Event>, value: unknown) => {
      handleChange(event, value as string);
    },
    [handleChange]
  );

  const handleRemoveOption = useCallback(
    (option: string, event: MouseEvent) => {
      event.stopPropagation();
      // Remove from localStorage and update options state
      const updatedOptions = LocalStorageManager.removeAutocompleteValue(fieldName, option);
      setOptions([...updatedOptions]); // Create a new array to trigger re-render
    },
    [fieldName]
  );

  const renderOption = useCallback(
    (props: HTMLAttributes<HTMLLIElement>, option: unknown): ReactNode => {
      const optionValue = option as string;
      return (
        <li {...props} key={optionValue}>
          <Box
            component='div'
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <span>{optionValue}</span>
            <Tooltip title={formatTranslation(intl, CommonTranslationKeys.remove)}>
              <IconButton
                size='small'
                sx={{
                  visibility: 'hidden',
                  '.MuiAutocomplete-option:hover &': {
                    visibility: 'visible',
                  },
                  color: '#757575',
                  '&:hover': {
                    color: '#000000',
                  },
                }}
                onClick={(event) => handleRemoveOption(optionValue, event)}
                aria-label='remove option'
              >
                <ClearIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </Box>
        </li>
      );
    },
    [handleRemoveOption]
  );

  return (
    <Tooltip
      title={
        typeof disabled === 'object'
          ? formatTranslation(intl, unsafeTranslationKey(disabled.reason) )
          : undefined
      }
    >
      <Box ref={ref}>
        <MemoAutocomplete
          value={defaultValue}
          options={options}
          id={id}
          disabled={Boolean(disabled)}
          freeSolo
          onChange={unifiedHandleChange}
          onBlur={onBlur}
          onInputChange={unifiedHandleChange}
          blurOnSelect={true}
          renderInput={renderInput}
          renderOption={renderOption}
          sx={sx}
        />
        <FormHelperText error={!!error}>
          {helperText || (reserveSpaceForHelperText && ' ')}
        </FormHelperText>
      </Box>
    </Tooltip>
  );
}

export const MemoAutocompleteWithText = memo(
  forwardRef(AutocompleteWithText)
);
